import { Formik, FormikProps } from 'formik';
import { TFunction } from 'i18next';
import { omit } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { RxTask } from 'src/js/helper/mobx-tasks';
import { NtpServerStatus } from 'src/js/model/ntp-server-status';
import { validateFormContext } from 'src/js/validator/validator';

import { DateTime, dateAndTimeSchema, dateAndTimeWithPtpSchema, mapDateTime } from './date-and-time-view-model';
import { DateTimeButtons } from './datetime-buttons';
import { PTP } from './ptp';
import { Server } from './server';

export interface DateAndTimeFormValues extends Omit<DateTime, 'chronydRunning'> {}

const excludedKeys = ['chronydRunning'];

interface GeneralFormProps {
  loading: boolean;
  data: DateTime;
  statisticsDisabled?: boolean;
  setManualTimeWasChanged: (changed: boolean) => void;
  translator: TFunction;
  onReboot?: VoidFunction;
  onStatistics?: VoidFunction;
  onSubmit?: (data: DateAndTimeFormValues) => Promise<any>;
  validateNtpServerTask?: RxTask<NtpServerStatus>;
}

export const DateAndTimeForm = (props: GeneralFormProps) => {
  const {
    data,
    loading,
    onReboot,
    onStatistics,
    setManualTimeWasChanged,
    statisticsDisabled,
    validateNtpServerTask,
    onSubmit,
    ...rest
  } = props;

  const { t } = useTranslation();

  const formikRef = useRef<FormikProps<DateAndTimeFormValues>>(null);

  const [model, setModel] = useState<DateAndTimeFormValues>({ ...(omit(excludedKeys, data) as DateAndTimeFormValues) });
  const [stats, setStats] = useState<DateAndTimeFormValues>({ ...(omit(excludedKeys, data) as DateAndTimeFormValues) });

  useEffect(() => {
    setStats((_existing) => {
      return data;
    });
  }, [data]);

  const onLocalSubmit = (userData: DateAndTimeFormValues) => {
    return onSubmit(userData).then((res) => {
      if (res.ok) {
        const datetime = mapDateTime(res.data);
        setStats(datetime);
        setModel(datetime);
        formikRef.current.resetForm({ values: datetime as DateAndTimeFormValues });
      }
    });
  };

  return (
    <Formik
      onSubmit={onLocalSubmit}
      initialValues={{ ...(omit(excludedKeys, model) as DateAndTimeFormValues) }}
      validationSchema={() => (systemInfoCapabilities.is2110() ? dateAndTimeWithPtpSchema(t) : dateAndTimeSchema(t))}
      enableReinitialize={false}
      innerRef={formikRef}
      {...rest}
    >
      {(formikProps) => {
        // console.log('formikProps.dirty', formikProps.dirty);
        // console.log('formikProps.touched', formikProps.touched);
        return (
          <>
            <DateTimeButtons
              onReboot={onReboot}
              onStatistics={onStatistics}
              statisticsDisabled={statisticsDisabled || !formikProps.values.ntp}
              onSubmit={() => validateFormContext(formikProps, t)}
              applyDisabled={!formikProps.dirty}
              isSubmitting={formikProps.isSubmitting}
            />
            <Server
              loading={loading}
              validateNtpServerTask={validateNtpServerTask}
              stats={stats}
              setManualTimeWasChanged={setManualTimeWasChanged}
              {...formikProps}
            />
            {systemInfoCapabilities.is2110() && <PTP formikProps={formikProps} initials={model} stats={stats} />}
          </>
        );
      }}
    </Formik>
  );
};
